import * as React from 'react';
import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import { observable } from "mobx";
import { Button, Modal, Col, Row } from 'antd';
import { resolve } from 'inversify-react';

import { FormatService } from 'src/core/services/format.service';
import { DropdownView } from 'src/core/ui/dropdown.view';
import { BooleanView } from "src/core/ui/boolean.view";
import { TextView } from "src/core/ui/text.view";
import { BillingTypeView } from './billing-type.view';
import { ContractCustomOccupanciesView } from './contract-custom-occupancies.view';

interface ContractStructureViewProps extends ComponentViewProps<StructureItem> {
	mode: ControlMode;
	type: 'cost' | 'price';
	contract: ContractItem;
	supplier: SupplierSummary;
	currency: CurrencySummary;
	currencies: CurrencySummary[];
	currencyChange: (newVal: any) => void;
	generatePrices: (priceCurrency: CurrencySummary) => void;
	onMarketChange?: (market: number) => void;
	selectedMarket?: number;
	refreshExchangeRates: (fromCurrencyId: number, toCurrencyId: number) => void;
	occupancyDataSet: DataSet<any>;
	ageBands: string[];
	getAllOccupancies: (maxPax: number) => void;
	newOccupancy: () => Promise<any>;
	productType: number;
}

@observer
export class ContractStructureView extends React.Component<ContractStructureViewProps, any> {
	@resolve
	formatService: FormatService;

	@observable
	showGeneratePriceDialog = false;

	costAndPriceAreDifferent = false;

	public constructor(props) {
		super(props);
	}

	public render() {
		if (this.props.currency == null) return null;
		const model = this.props.model;

		let optionsMode = [
			{ value: 'Amount', label: 'Amount' },
			{ value: 'Percent', label: 'Percent' }
		];
		let optionsDate = [
			{ value: 'AddTogether', label: 'Add Together' },
			{ value: 'Average', label: 'Average' },
			{ value: 'FirstDate', label: 'First Date' },
			{ value: 'LastDate', label: 'Last Date' }
		];
		const marketOptions = [{
			value: 0,
			label: 'All markets'
		}].concat((this.props.contract.markets || []).map(e => ({
			value: e.marketId,
			label: e.marketName
		})));
		let currencySymbol = this.props.currency ? this.props.currency.symbol : '$';
		let descriptionCurrency = undefined;
		let descriptionMargin = undefined;
		let defaultMargin = (model as PriceStructureItem).defaultMargin || 0.25;
		const cost: CostItem = this.props.contract.dynamicProperties['cost'];
		let selectedCurrency = this.props.currency == null ? null : this.props.currency.id;
		if (this.props.type == 'price' && this.props.selectedMarket > 0 && this.props.contract.markets && this.props.contract.markets.length > 0) {
			const market = this.props.contract.markets.find(e => e.marketId == this.props.selectedMarket);
			const currency = this.props.currencies.find(e => e.id == market.marketCurrencyId);
			descriptionCurrency = `Currency used in sell prices is defined from market: ${currency.iso3Name}`;
			switch (market.marketMarginPolicy) {
				case 0:
					defaultMargin = market.marketDefaultMargin;
					break;
				case 1:
					defaultMargin = this.props.supplier.defaultMargin;
					break;
				case 2:
					defaultMargin = parseFloat(market.marketDefaultMargin.toString()) + parseFloat(this.props.supplier.defaultMargin.toString()); // *They come from the server as string, but in client they are used as 'number'
					break;
			}
			descriptionMargin = `Default Market Margin: ${this.formatService.toString('percent-full', defaultMargin)}`;
			selectedCurrency = market.marketCurrencyId;
		}
		const currenciesAreDifferent = cost.elements.length == 0 || this.props.currency == null
			? false
			: cost.elements[0].currency != selectedCurrency;
		return (
			<div>
				<Modal
					visible={this.showGeneratePriceDialog}
					onCancel={this.closeDialog}
					title="Warning!"
					maskClosable={false}
					footer={[
						<Button key='confirm' type="primary" onClick={this.acceptDialog}>Ok</Button>,
						<Button key='cancel' onClick={this.closeDialog}>Cancel</Button>
					]}
				>

					{
						this.costAndPriceAreDifferent
							? 'The sell price structure is different from the cost price structure. All sell price for the selected market will be generated, but conversion could be undesired. Are you sure you want to continue?'
							: 'This action will regenerate the sell prices for the selected market. Are you sure you want to continue?'
					}
				</Modal>

				<Row gutter={16}>
					<Col xs={24} lg={12}>
						<DropdownView
							disabled={this.props.mode == 'display' || descriptionCurrency != null}
							label='Currency'
							required
							model={this.props.currency ? this.props.currency.id : null}
							onChanged={newVal => this.props.currencyChange(newVal)}
							keyField='id'
							textField='iso3Name'
							options={this.props.currencies.slice()}
							description={descriptionCurrency}
						/>
					</Col>
					<Col xs={24} lg={12}>
						<BillingTypeView
							mode={this.props.mode}
							model={model.type}
							required
							onChanged={(item) => model.type = item.value}
						/>
					</Col>
				</Row>
				{
					 this.props.productType == 6 &&
					<Row gutter={16}>
						<Col xs={24} lg={12}>
							<DropdownView
								disabled={this.props.mode == 'display'}
								label='Mode:'
								model={model.mode}
								required
								onChanged={(item) => model.mode = item.value}
								options={optionsMode}
							/>
						</Col>
						<Col xs={24} lg={12}>
							<label>&nbsp;</label>
							<br />
							<Button.Group>
								<Button
									icon={model.sign == 'Up' ? 'plus-circle-o' : 'minus-circle-o'}
									disabled={this.props.mode == 'display'}
									onClick={() => model.sign = model.sign == 'Up' ? 'Down' : 'Up'}
								>{model.sign == 'Up' ? 'Supplement' : 'Discount'}</Button>
								<Button
									icon={model.required ? 'lock' : 'unlock'}
									disabled={this.props.mode == 'display'}
									onClick={() => {
										model.required = !model.required;
									}}
								>{model.required ? 'Required' : 'Optional'}</Button>
								{
									model.mode == 'Amount' &&
									<Button
										icon={model.replaceBasePrice ? 'login' : 'logout'}
										disabled={this.props.mode == 'display'}
										onClick={() => {
											model.replaceBasePrice = !model.replaceBasePrice;
										}}
									>{model.replaceBasePrice ? 'Replace base price' : "Don't replace base price"}</Button>
								}
							</Button.Group>
						</Col>
					</Row>
				}
				<Row gutter={16}>
					<Col xs={24} lg={12}>
						<DropdownView
							disabled={this.props.mode == 'display'}
							label='Date band crossing behavior:'
							model={model.crossingDateBandBehaviour}
							required
							onChanged={(item) => model.crossingDateBandBehaviour = item.value}
							options={optionsDate}
						/>
					</Col>
					<Col xs={12} lg={6}>
						<TextView mode={this.props.mode}
							label='Max Pax'
							value={model.maxPax}
							onChanged={newVal => {
								model.maxPax = newVal;
							}}
							required={!model.isCustomOccupancyConfig}

						/>
					</Col>
					<Col xs={12} lg={6}>
						<BooleanView
							mode={this.props.occupancyDataSet && this.props.ageBands.length > 0 && model.maxPax != null ? this.props.mode : 'display'}
							label='Allow specific pax config'
							value={model.isCustomOccupancyConfig}
							onChanged={newVal => {
								model.isCustomOccupancyConfig = newVal;
							}}
						/>
					</Col>
				</Row>
				<Row gutter={16}>
					{
						this.props.type != "cost" && <Col xs={24} lg={12}>
							<DropdownView
								required
								label="Market:"
								disabled={this.props.mode === 'display'}
								model={this.props.selectedMarket}
								onChanged={(item) => this.props.onMarketChange(item.value)}
								options={marketOptions}
							/>
						</Col>
					}
					{
						this.props.type != "cost" && currenciesAreDifferent && <Col xs={12} lg={6}>
							<TextView mode={this.props.mode}
								required
								label='Exchange Rate'
								value={(model as PriceStructureItem).currencyExchangeRate}
								onChanged={newVal => {
									newVal = newVal != null && newVal != '' ? parseFloat(newVal) : null;
									(model as PriceStructureItem).currencyExchangeRate = newVal;
								}}
								suffix={currencySymbol}
								addonClass='addon-button'
								addonAfter={this.props.type == 'price'
									? <Button className='square-button'
										type='primary'
										icon='reload'
										onClick={() => {
											this.props.refreshExchangeRates(cost.elements[0].currency, selectedCurrency);
										}} disabled={this.props.mode == 'display'}>Refresh rate</Button>
									: undefined
								}
							/>
						</Col>
					}
					{
						this.props.type != "cost" && <Col xs={12} lg={6}>
							<TextView mode={this.props.mode}
								label='Percent'
								value={(model as PriceStructureItem).defaultMargin}
								onChanged={newVal => {
									newVal = newVal != null ? parseFloat(newVal) : null;
									(model as PriceStructureItem).defaultMargin = newVal;
								}}
								description={descriptionMargin}
								format='percent'
								suffix='%'
								addonClass='addon-button'
								addonAfter={this.props.type == 'price'
									? <Button
										className='square-button'
										type={this.props.selectedMarket == null || defaultMargin == (model as PriceStructureItem).defaultMargin ? 'primary' : 'danger'}
										onClick={() => {
											if (cost.structure.type != this.props.model.type && cost.structure.mode != this.props.model.mode && cost.structure.sign != this.props.model.sign) {
												this.costAndPriceAreDifferent = true;
											} else {
												this.costAndPriceAreDifferent = false;
											}
											this.showGeneratePriceDialog = true;
										}}
										disabled={this.props.mode == 'display' || this.props.currency == null}
									>
										Generate prices
									</Button>
									: undefined
								}
							/>
						</Col>
					}
					{
						this.props.type != "cost" && <Col xs={12} lg={6}>
							<BooleanView
								mode={this.props.mode}
								label='Use only with Market currency'
								value={model.useOnlyWithMarketCurrency}
								onChanged={newVal => {
									model.useOnlyWithMarketCurrency = newVal;
								}}
							/>
						</Col>
					}
				</Row>
				{
					model.isCustomOccupancyConfig && this.props.occupancyDataSet &&
					<ContractCustomOccupanciesView
						ageBands={this.props.ageBands}
						getAllOccupancies={this.props.getAllOccupancies}
						mode={this.props.mode}
						model={model.maxPax}
						newOccupancy={this.props.newOccupancy}
						occupancyDataSet={this.props.occupancyDataSet}
						setBusy={this.props.setBusy}
					/>
				}
			</div>
		)
	}

	@autobind
	private closeDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		ev.preventDefault();
		this.showGeneratePriceDialog = false;
	}

	@autobind
	private acceptDialog(ev?: React.MouseEvent<HTMLButtonElement>) {
		let currency = this.props.currency;
		if (this.props.selectedMarket > 0) {
			const market = this.props.contract.markets.find(e => e.marketId == this.props.selectedMarket);
			currency = this.props.currencies.find(e => market.marketCurrencyId == e.id);
		}
		this.props.generatePrices(currency);
		this.closeDialog(ev);
	}
}



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-structure.view.tsx